import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useClipboard } from "../../hooks/useClipboard";
import StatusPill, { STATUSES } from "../../components/StatusPill";
import { 
  Search, LayoutGrid, List, Briefcase, FileWarning, CalendarClock, 
  AlertCircle, CheckCircle2, ExternalLink, Share2, Copy, Check, Wand2
} from "lucide-react";
import { useAgency } from "../../contexts/AgencyContext";
import { UniversalIntakeForm } from "../../components/ui/universal-intake-form";
import { toast } from "sonner";

const VISIBLE_LANES = STATUSES;

// Chip configurations for visual indicators
function getApplicantChips(app) {
  const chips = [];
  
  // Check for scheduled interview (next_interview populated by backend)
  if (app.next_interview) {
    const ivDate = new Date(app.next_interview.scheduled_at);
    const today = new Date();
    const isToday = ivDate.toDateString() === today.toDateString();
    chips.push({
      key: "interview",
      label: isToday ? "Interview Today" : `Interview ${ivDate.toLocaleDateString()}`,
      icon: CalendarClock,
      variant: isToday ? "info" : "neutral",
    });
  }
  
  // Check for missing documents (document_count < 4 expected docs)
  // Backend returns document_count, we expect at least 4 core docs
  if (app.document_count !== undefined && app.document_count < 4 && 
      !["employee", "not_selected"].includes(app.status)) {
    chips.push({
      key: "missing-docs",
      label: `${4 - app.document_count} Doc${4 - app.document_count !== 1 ? "s" : ""} Missing`,
      icon: FileWarning,
      variant: "warning",
    });
  }
  
  // Action required for certain statuses
  if (app.status === "application_received") {
    chips.push({
      key: "action",
      label: "Needs Review",
      icon: AlertCircle,
      variant: "action",
    });
  }
  
  // Ready for next step
  if (app.status === "interview_complete" || app.status === "offer_accepted") {
    chips.push({
      key: "ready",
      label: app.status === "offer_accepted" ? "Ready to Onboard" : "Ready for Decision",
      icon: CheckCircle2,
      variant: "success",
    });
  }
  
  return chips;
}

const CHIP_STYLES = {
  neutral: "bg-secondary text-muted-foreground border-border",
  info: "bg-blue-50 text-blue-700 border-blue-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  action: "bg-primary-c/10 text-primary-c border-primary-c/20",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function HiringQueue() {
  const [view, setView] = useState("kanban");
  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAIIntake, setShowAIIntake] = useState(false);
  const { copied, copyToClipboard } = useClipboard();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agency } = useAgency();
  
  // Support status filter from URL params (e.g., ?status=application_received)
  const statusFilter = searchParams.get("status");

  const load = async () => {
    setLoading(true);
    try {
      const params = { search: search || undefined };
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/hiring/applications", { params });
      setApplications(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
  }, [search, statusFilter]);

  const byStatus = useMemo(() => {
    const m = Object.fromEntries(VISIBLE_LANES.map((s) => [s, []]));
    for (const a of applications) {
      (m[a.status] || (m[a.status] = [])).push(a);
    }
    return m;
  }, [applications]);
  
  const handleCopyLink = () => {
    copyToClipboard(`${window.location.origin}/apply/${agency?.id}`);
  };

  return (
    <div className="px-4 md:px-8 py-6 md:py-8">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2">Hiring & Onboarding</div>
          <h1 className="heading-display" data-testid="hiring-title">Applicant Queue</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Review applications, schedule interviews, and move candidates through the pipeline.
          </p>
        </div>
        {agency && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAIIntake(true)}
              className="btn-secondary text-sm"
              data-testid="ai-intake-applicant-btn"
            >
              <Wand2 className="w-4 h-4" /> AI Quick Entry
            </button>
            <a
              href={`/apply/${agency.id}`}
              target="_blank"
              rel="noreferrer"
              className="btn-secondary text-sm"
              data-testid="preview-application-link"
            >
              <ExternalLink className="w-4 h-4" /> Preview Form
            </a>
            <button
              onClick={handleCopyLink}
              className="btn-primary text-sm"
              data-testid="copy-application-link"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied!" : "Copy Link"}
            </button>
          </div>
        )}
      </header>

      {/* AI Intake Modal for Quick Applicant Entry */}
      {showAIIntake && (
        <UniversalIntakeForm
          entityType="applicant"
          isModal={true}
          onClose={() => setShowAIIntake(false)}
          onDataExtracted={async (extractedData) => {
            try {
              // Create application with extracted data
              const response = await api.post('/hiring/applications', {
                first_name: extractedData.first_name,
                last_name: extractedData.last_name,
                email: extractedData.email,
                phone: extractedData.phone,
                date_of_birth: extractedData.date_of_birth,
                address: extractedData.address,
                city: extractedData.city,
                state: extractedData.state,
                zip_code: extractedData.zip_code,
                years_experience: extractedData.years_experience,
                certifications: extractedData.certifications,
                languages: extractedData.languages,
                availability: extractedData.availability,
                transportation: extractedData.transportation,
                work_authorization: extractedData.work_authorization,
                desired_hourly_rate: extractedData.desired_hourly_rate,
                notes: extractedData.notes,
                status: 'application_received',
                agency_id: agency?.id,
              });
              
              if (response?.data?.id) {
                toast.success('Applicant created successfully!');
                setShowAIIntake(false);
                load(); // Refresh the list
                navigate(`/hiring/applicant/${response.data.id}`);
              }
            } catch (err) {
              console.error('Failed to create applicant:', err);
              toast.error('Failed to create applicant. Please try again.');
            }
          }}
        />
      )}

      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or code…"
            className="input-base pl-9"
            data-testid="hiring-search"
          />
        </div>
        {statusFilter && (
          <button
            onClick={() => navigate("/hiring")}
            className="btn-secondary text-xs"
            data-testid="clear-filter"
          >
            Clear filter
          </button>
        )}
        <div className="ml-auto inline-flex p-1 bg-secondary rounded-lg" data-testid="view-toggle">
          <button
            onClick={() => setView("kanban")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors inline-flex items-center gap-1.5 ${view === "kanban" ? "bg-white shadow-sm" : "text-muted-foreground"}`}
            data-testid="view-kanban"
          >
            <LayoutGrid className="w-4 h-4" /> Board
          </button>
          <button
            onClick={() => setView("table")}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors inline-flex items-center gap-1.5 ${view === "table" ? "bg-white shadow-sm" : "text-muted-foreground"}`}
            data-testid="view-table"
          >
            <List className="w-4 h-4" /> List
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Loading…</div>
      ) : applications.length === 0 ? (
        <EmptyState statusFilter={statusFilter} agencyId={agency?.id} />
      ) : view === "kanban" ? (
        <KanbanView byStatus={byStatus} onCardClick={(id) => navigate(`/hiring/${id}`)} />
      ) : (
        <TableView applications={applications} />
      )}
    </div>
  );
}

function EmptyState({ statusFilter, agencyId }) {
  if (statusFilter) {
    return (
      <div className="surface p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-4">
          <Search className="w-8 h-8 text-muted-foreground/50" />
        </div>
        <h3 className="font-semibold text-lg">No matching applicants</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
          No applicants found with the current filter. Try adjusting your search or clearing the filter.
        </p>
        <Link to="/hiring" className="btn-secondary mt-4 inline-flex">
          View all applicants
        </Link>
      </div>
    );
  }
  
  return (
    <div className="surface p-12 text-center">
      <div className="w-16 h-16 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-4">
        <Briefcase className="w-8 h-8 text-muted-foreground/50" />
      </div>
      <h3 className="font-semibold text-lg">Your hiring pipeline is empty</h3>
      <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
        Share your public application link with prospective caregivers to start receiving applications.
      </p>
      {agencyId && (
        <a
          href={`/apply/${agencyId}`}
          target="_blank"
          rel="noreferrer"
          className="btn-primary mt-4 inline-flex"
          data-testid="empty-state-apply-link"
        >
          <ExternalLink className="w-4 h-4" /> Preview Application Form
        </a>
      )}
    </div>
  );
}

function KanbanView({ byStatus, onCardClick }) {
  return (
    <div className="flex gap-4 overflow-x-auto scrollbar-thin pb-4 -mx-2 px-2">
      {VISIBLE_LANES.map((s) => {
        const items = byStatus[s] || [];
        return (
          <div key={s} className="w-[300px] shrink-0" data-testid={`kanban-column-${s}`}>
            <div className="flex items-center justify-between mb-2 px-1">
              <StatusPill status={s} />
              <div className="text-xs text-muted-foreground tabular-nums font-medium">{items.length}</div>
            </div>
            <div className="bg-secondary/40 rounded-xl p-2 space-y-2 min-h-[120px] border border-border">
              {items.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-8 px-4">
                  <div className="w-10 h-10 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-2">
                    <Briefcase className="w-5 h-5 text-muted-foreground/40" />
                  </div>
                  No candidates
                </div>
              ) : (
                items.map((a) => (
                  <ApplicantCard key={a.id} app={a} onClick={() => onCardClick(a.id)} />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ApplicantCard({ app, onClick }) {
  const chips = getApplicantChips(app);
  
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-card border border-border rounded-lg p-3 hover:shadow-md transition-all hover:border-foreground/20 group"
      data-testid={`applicant-card-${app.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-sm group-hover:text-primary-c transition-colors">
            {app.first_name} {app.last_name}
          </div>
          <div className="text-xs text-muted-foreground truncate mt-0.5">{app.email}</div>
        </div>
      </div>
      
      {/* Indicator Chips */}
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {chips.slice(0, 2).map((chip) => {
            const Icon = chip.icon;
            return (
              <span 
                key={chip.key}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${CHIP_STYLES[chip.variant]}`}
                data-testid={`chip-${chip.key}-${app.id}`}
              >
                <Icon className="w-3 h-3" />
                {chip.label}
              </span>
            );
          })}
        </div>
      )}
      
      <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
        <span className="font-mono">{app.tracking_code}</span>
        <span>{new Date(app.submitted_at).toLocaleDateString()}</span>
      </div>
    </button>
  );
}

function TableView({ applications }) {
  return (
    <div className="surface overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/40">
            <tr className="text-left">
              <th className="px-4 py-3 font-semibold">Applicant</th>
              <th className="px-4 py-3 font-semibold">Tracking</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Indicators</th>
              <th className="px-4 py-3 font-semibold">Submitted</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {applications.map((a) => {
              const chips = getApplicantChips(a);
              return (
                <tr key={a.id} className="border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors" data-testid={`applicant-row-${a.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.first_name} {a.last_name}</div>
                    <div className="text-xs text-muted-foreground">{a.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{a.tracking_code}</td>
                  <td className="px-4 py-3"><StatusPill status={a.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {chips.length === 0 ? (
                        <span className="text-xs text-muted-foreground">—</span>
                      ) : (
                        chips.slice(0, 2).map((chip) => {
                          const Icon = chip.icon;
                          return (
                            <span 
                              key={chip.key}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${CHIP_STYLES[chip.variant]}`}
                            >
                              <Icon className="w-3 h-3" />
                              {chip.label}
                            </span>
                          );
                        })
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(a.submitted_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/hiring/${a.id}`} className="btn-ghost text-sm px-2 py-1" data-testid={`view-${a.id}`}>
                      View →
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
